import { useState, type ReactNode } from 'react';
import { Collapse, IconButton, Stack } from '@mui/material';

import { PDFViewer } from 'features/pdf-viewer';

import Dialog from 'shared/components/Dialog';
import useSelector from 'shared/hooks/useSelector';
import CloseIcon from 'shared/icons/Close';
import InfoIcon from 'shared/icons/Info';

import { Container, ShowPDFButton, Text } from './Instruction.style';

type InstructionButtonProps = {
  children: ReactNode;
  url: string;
};

const InstructionButton = ({ children, url }: InstructionButtonProps) => {
  const [isOpenInstruction, setIsOpenInstruction] = useState(false);

  const handleOpenInstruction = () => {
    setIsOpenInstruction(true);
  };

  const handleCloseInstruction = () => {
    setIsOpenInstruction(false);
  };

  return (
    <>
      <ShowPDFButton onClick={handleOpenInstruction} role="button">
        {children}
      </ShowPDFButton>
      <Dialog
        fullHeight
        fullWidth
        maxWidth="lg"
        open={isOpenInstruction}
        onClose={handleCloseInstruction}
      >
        <PDFViewer url={url} />
      </Dialog>
    </>
  );
};

const Instruction = () => {
  const { repairInstruction, repairInstructionEng } = useSelector((state) => ({
    repairInstruction: state.app.repairInstruction,
    repairInstructionEng: state.app.repairInstructionEng,
  }));

  const [isExpanded, setIsExpanded] = useState(true);

  const toggleInstructionVisibility = () => {
    setIsExpanded((prevState) => !prevState);
  };

  return (
    <Container direction="row" expanded={isExpanded}>
      {isExpanded && (
        <IconButton
          onClick={toggleInstructionVisibility}
          sx={{
            position: 'absolute',
            top: '5px',
            right: '5px',
            padding: '2px',
            background: 'transparent',
          }}
        >
          <CloseIcon
            sx={{
              fontSize: '1.3rem',
              color: (theme) => theme.palette.primary.main,
            }}
          />
        </IconButton>
      )}
      <Stack alignItems="flex-start" direction="row" spacing={1.5}>
        <IconButton
          onClick={toggleInstructionVisibility}
          sx={{ padding: '2px', background: 'transparent' }}
        >
          <InfoIcon sx={{ color: (theme) => theme.palette.primary.main }} />
        </IconButton>
        <Collapse in={isExpanded}>
          <Stack spacing={2}>
            <Text>
              Ennen muutostyöilmoituksen tekemistä tutustu muutostyöohjeeseen.
              Ohjeessa on kuvattu tarkasti, mitä eri muutostöissä tulee
              huomioida.
            </Text>
            <Text>
              Syötäthän muutostyöilmoituksen tiedot mahdollisimman kattavasti,
              sillä se sujuvoittaa käsittelyä. Lisää muutostyötä koskevat
              dokumentit ilmoituksen liitteisiin.
            </Text>
            <Text>
              Muistutamme, että muutostyöilmoituksen käsittely saattaa kestää
              2-4 viikkoa tai poikkeustapauksissa jopa 2 kuukautta, varsinkin
              jos tarvitaan lisäselvityksiä. Kiireellisissä tapauksissa ole
              suoraan yhteydessä myös isännöitsijään.
            </Text>
            <Text>
              Remonttia ei saa aloittaa, ennen kuin muutostyöilmoitus on
              käsitelty ja aloituslupa saatu taloyhtiöltä, sekä valvottavien
              muutostöiden valvonnasta sovittu valvojan kanssa.
            </Text>
            <Stack
              alignItems={{
                sm: 'center',
                xs: 'flex-start',
              }}
              direction={{
                sm: 'row',
                xs: 'column',
              }}
              spacing={2}
            >
              {repairInstruction && (
                <InstructionButton url={repairInstruction}>
                  Muutostyöohje.pdf
                </InstructionButton>
              )}
              {repairInstructionEng && (
                <InstructionButton url={repairInstructionEng}>
                  Instructions for renovation work.pdf
                </InstructionButton>
              )}
            </Stack>
          </Stack>
        </Collapse>
      </Stack>
    </Container>
  );
};

export default Instruction;
